import React, { useState, useEffect } from 'react';
import { X, ArrowRight, Users, Building2, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../services/api';

function TransferModal({ conversationId, currentSectorId, onClose, onSuccess }) {
  const [sectors, setSectors] = useState([]);
  const [operators, setOperators] = useState([]);
  const [selectedSector, setSelectedSector] = useState('');
  const [selectedOperator, setSelectedOperator] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    loadSectors();
  }, [conversationId]);

  useEffect(() => {
    if (selectedSector) {
      loadOperators(selectedSector);
    } else {
      setOperators([]);
      setSelectedOperator('');
    }
  }, [selectedSector]);

  const loadSectors = async () => {
    try {
      const res = await api.get(`/api/conversations/${conversationId}/available-sectors`);
      setSectors(res.data);
    } catch (err) {
      console.error('Erro ao carregar setores:', err);
    }
  };

  const loadOperators = async (sectorId) => {
    setLoadingOperators(true);
    try {
      const res = await api.get(`/api/conversations/sector/${sectorId}/available-operators`);
      setOperators(res.data);
    } catch (err) {
      console.error('Erro ao carregar operadores:', err);
    } finally {
      setLoadingOperators(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedSector) {
      alert('Selecione um setor de destino');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/api/conversations/${conversationId}/transfer`, {
        sector_id: parseInt(selectedSector),
        user_id: selectedOperator ? parseInt(selectedOperator) : null,
        reason: reason || null
      });
      
      onSuccess();
      onClose();
    } catch (err) {
      alert('Erro ao transferir: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-md shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-slate-700">
          <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">
            Transferir Conversa
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-slate-300">
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Setor de Destino */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              <Building2 size={16} className="inline mr-1" />
              Setor de Destino *
            </label>
            <select
              value={selectedSector}
              onChange={(e) => setSelectedSector(e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none dark:bg-slate-700 dark:text-slate-100"
            >
              <option value="">Selecione um setor</option>
              {sectors.map(sector => (
                <option key={sector.id} value={sector.id}>
                  {sector.name}
                  {sector.id === currentSectorId && ' (Setor Atual)'}
                  {sector.online_operators > 0 && ` • ${sector.online_operators} online`}
                </option>
              ))}
            </select>
          </div>

          {/* Operador (opcional) */}
          {selectedSector && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                <Users size={16} className="inline mr-1" />
                Atribuir para Operador (opcional)
              </label>
              {loadingOperators ? (
                <div className="text-center py-4 text-gray-400 dark:text-slate-500">
                  Carregando operadores...
                </div>
              ) : operators.length === 0 ? (
                <div className="text-center py-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                  <AlertCircle size={20} className="mx-auto mb-2 text-yellow-600 dark:text-yellow-400" />
                  <p className="text-sm text-yellow-700 dark:text-yellow-300">
                    Nenhum operador online neste setor
                  </p>
                  <p className="text-xs text-yellow-600 dark:text-yellow-400 mt-1">
                    A conversa será transferida para o setor sem atribuição
                  </p>
                </div>
              ) : (
                <select
                  value={selectedOperator}
                  onChange={(e) => setSelectedOperator(e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none dark:bg-slate-700 dark:text-slate-100"
                >
                  <option value="">Apenas transferir para o setor</option>
                  {operators.map(op => (
                    <option key={op.id} value={op.id}>
                      {op.name} • {op.active_conversations} conversas ativas
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Motivo (opcional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
              Motivo (opcional)
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Cliente precisa de atendimento especializado..."
              rows={3}
              className="w-full p-3 border border-gray-300 dark:border-slate-600 rounded-xl text-sm focus:ring-2 focus:ring-green-500 outline-none resize-none dark:bg-slate-700 dark:text-slate-100"
            />
          </div>

          {/* Info */}
          {selectedSector && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <CheckCircle size={14} className="inline mr-1" />
                A conversa será transferida e o histórico será registrado
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-slate-700">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-700"
          >
            Cancelar
          </button>
          <button
            onClick={handleTransfer}
            disabled={!selectedSector || loading}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm font-bold hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Transferindo...
              </>
            ) : (
              <>
                <ArrowRight size={16} />
                Transferir
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default TransferModal;